// eslint-disable-next-line no-unused-vars
const analyticsTypes = require('../jsdoc-types');
const {
  StorageDecorators,
  StorageFactory,
  STORAGE_BACKEND,
} = require('../../common/utils/storage');
const {
  isVariationGroupId,
  isVariationProductId,
} = require('../../common/helpers/productIdHelpers');
const { isDoubleLook } = require('../../common/utils/doubleLookUtils');

let fallbackItemList;

/**
 * @typedef {Object} ItemListAndPosition
 * @property {string} list - The list content as a string.
 * @property {number} position - The current index in the list.
 */

/**
 * Creates an object containing a string list and a selected index.
 *
 * @param {string} list - A string representing the list of a product.
 * @param {number} position - The one-based position of the product in that list.
 * @returns {ItemListAndPosition} Object with list and position for analytics tracking.
 */
function createItemListAndPosition(list, position) {
  return { list, position };
}

/**
 * Check whether the param is an instance of ItemListAndPosition
 * @param {any} obj target
 * @returns {void} true if the target is an ItemListAndPosition
 * @throws {TypeError}
 */
function assertItemListAndPosition(obj) {
  const isValid = 'list' in obj && 'position' in obj;
  if (!isValid) {
    throw new TypeError('Illegal argument: item is not a ItemListAndPosition');
  }
}

const decorateStorageWithOneYearTTL = StorageDecorators.ttlDecorator.create(
  365 * 24 * 60 * 60 * 1000
);

const decorateStorageWithVariationGroupIdValidator =
  StorageDecorators.validationDecorator.create({
    validateKey: (key) => {
      if (!isVariationGroupId(key) && !isDoubleLook(key)) {
        throw new TypeError(
          `Illegal argument: "${key}" is not a variation group ID `
        );
      }
    },
    validateValue: assertItemListAndPosition,
  });

const decorateStorageWithVariantIdValidator =
  StorageDecorators.validationDecorator.create({
    validateKey: (key) => {
      if (!isVariationProductId(key)) {
        throw new TypeError(`Illegal argument: "${key}" is not a variant ID `);
      }
    },
    validateValue: assertItemListAndPosition,
  });

const impressionItemListStorage = StorageFactory.createStorage({
  backend: STORAGE_BACKEND.SESSION_STORAGE,
  name: 'ga.itemList.impressions',
  decorators: [decorateStorageWithVariationGroupIdValidator],
});

const clickedItemListStorage = StorageFactory.createStorage({
  backend: STORAGE_BACKEND.SESSION_STORAGE,
  name: 'ga.itemList.clicked',
  decorators: [decorateStorageWithVariationGroupIdValidator],
});

const cartItemListStorage = StorageFactory.createStorage({
  backend: STORAGE_BACKEND.LOCAL_STORAGE,
  name: 'ga.itemList.cart',
  decorators: [
    decorateStorageWithVariantIdValidator,
    decorateStorageWithOneYearTTL,
  ],
});

/**
 * Returns the list considered as fallback ('direct', 'other', 'home'...).
 * @returns {string|null} The name of the list
 */
function getFallbackItemList() {
  return fallbackItemList;
}

/**
 * Saves the list considered as fallback ('direct', 'other', 'home'...).
 * @param {string} listName the name of the list to set as fallback
 * @returns {void}
 */
function setFallbackItemList(listName) {
  fallbackItemList = listName;
}

module.exports = {
  cartItemListStorage,
  clickedItemListStorage,
  createItemListAndPosition,
  getFallbackItemList,
  impressionItemListStorage,
  setFallbackItemList,
};
